/**
 * FERP Portal - Data Source Resolver
 *
 * Resolves data sources for dynamic field options.
 * Supports static, GraphQL, FHIR, API, and computed sources.
 *
 * CONSTRAINTS:
 * - All data access through repositories
 * - Tenant-scoped queries
 * - Caching for performance
 */

import { useCallback, useRef, useState, useEffect } from 'react';
// eslint-disable-next-line no-restricted-imports -- Data layer module requires direct Apollo access
import { useApolloClient } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useTenantId } from '@/auth';
import type { DataSource, OptionItem } from './types';

// ============================================================================
// TYPES
// ============================================================================

interface CacheEntry {
  data: OptionItem[];
  timestamp: number;
  ttl: number;
}

type DataSourceResolver = (source: DataSource) => Promise<OptionItem[]>;

// ============================================================================
// CACHE
// ============================================================================

/**
 * Simple in-memory cache for data source results
 */
class DataSourceCache {
  private cache = new Map<string, CacheEntry>();

  /**
   * Generate cache key for a data source
   */
  private getKey(source: DataSource): string {
    return JSON.stringify({
      type: source.type,
      query: source.query,
      endpoint: source.endpoint,
      fhirResourceType: source.fhirResourceType,
      expression: source.expression,
      variables: source.variables,
    });
  }

  /**
   * Get cached data if valid
   */
  get(source: DataSource): OptionItem[] | null {
    const key = this.getKey(source);
    const entry = this.cache.get(key);

    if (!entry) return null;

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Set cached data
   */
  set(source: DataSource, data: OptionItem[], ttl: number = 60000): void {
    const key = this.getKey(source);
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  /**
   * Clear all cached data
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Clear cached data for a specific source
   */
  invalidate(source: DataSource): void {
    const key = this.getKey(source);
    this.cache.delete(key);
  }
}

// Global cache instance
const globalCache = new DataSourceCache();

// ============================================================================
// RESOLVERS
// ============================================================================

/**
 * Resolve static data source
 */
function resolveStatic(source: DataSource): OptionItem[] {
  return source.options || [];
}

/**
 * Extract value from nested object path
 */
function getNestedValue(obj: any, path: string): any {
  const parts = path.split('.');
  let current = obj;

  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    current = current[part];
  }

  return current;
}

/**
 * Transform GraphQL response to OptionItem[]
 */
function transformGraphQLResponse(
  data: any,
  valuePath: string = 'id',
  labelPath: string = 'name'
): OptionItem[] {
  if (!data) return [];

  // Handle array response
  if (Array.isArray(data)) {
    return data?.map((item) => ({
      value: getNestedValue(item, valuePath) ?? '',
      label: getNestedValue(item, labelPath) ?? '',
      disabled: item.disabled ?? false,
      group: item.group ?? undefined,
    }));
  }

  // Handle object with edges (Relay-style)
  if (data.edges) {
    return data?.edges.map((edge: any) => ({
      value: getNestedValue(edge.node, valuePath) ?? '',
      label: getNestedValue(edge.node, labelPath) ?? '',
      disabled: edge.node.disabled ?? false,
      group: edge.node.group ?? undefined,
    }));
  }

  // Handle object with nodes
  if (data.nodes) {
    return data?.nodes.map((node: any) => ({
      value: getNestedValue(node, valuePath) ?? '',
      label: getNestedValue(node, labelPath) ?? '',
      disabled: node.disabled ?? false,
      group: node.group ?? undefined,
    }));
  }

  // Handle object with items
  if (data.items) {
    return data?.items.map((item: any) => ({
      value: getNestedValue(item, valuePath) ?? '',
      label: getNestedValue(item, labelPath) ?? '',
      disabled: item.disabled ?? false,
      group: item.group ?? undefined,
    }));
  }

  return [];
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for resolving data sources
 */
export function useDataSourceResolver(): DataSourceResolver {
  const apolloClient = useApolloClient();
  const tenantId = useTenantId();
  const cacheRef = useRef(globalCache);

  /**
   * Resolve GraphQL data source
   */
  const resolveGraphQL = useCallback(
    async (source: DataSource): Promise<OptionItem[]> => {
      if (!source.query) {
        console.warn('GraphQL data source missing query');
        return [];
      }

      try {
        const { data } = await apolloClient.query<Record<string, any>>({
          query: gql`
            ${source.query}
          `,
          variables: {
            ...source.variables,
            tenantId,
          },
          fetchPolicy: 'network-only',
        });

        // Find the first non-null value in the response
        // @ts-expect-error No overload match
        const responseData = Object.values(data).find((v) => v !== null);

        return transformGraphQLResponse(
          responseData,
          source.valuePath || 'id',
          source.labelPath || 'name'
        );
      } catch (error) {
        console.error('GraphQL data source error:', error);
        return [];
      }
    },
    [apolloClient, tenantId]
  );

  /**
   * Resolve FHIR data source
   */
  const resolveFHIR = useCallback(
    async (source: DataSource): Promise<OptionItem[]> => {
      if (!source.fhirResourceType) {
        console.warn('FHIR data source missing resourceType');
        return [];
      }

      // Build FHIR search URL
      const searchParams = new URLSearchParams(source.fhirSearchParams || {});
      const url = `/fhir/${source.fhirResourceType}?${searchParams.toString()}`;

      try {
        // eslint-disable-next-line no-restricted-syntax -- FHIR data sources are external APIs
        const response = await fetch(url, {
          headers: {
            Accept: 'application/fhir+json',
            'X-Tenant-ID': tenantId || '',
          },
        });

        if (!response.ok) {
          throw new Error(`FHIR request failed: ${response.status}`);
        }

        const bundle = await response.json();

        // Transform FHIR Bundle to OptionItem[]
        if (bundle.entry) {
          return bundle.entry.map((entry: any) => {
            const resource = entry.resource;
            return {
              value: resource.id,
              label:
                resource.name?.[0]?.text ||
                resource.name?.[0]?.given?.join(' ') + ' ' + resource.name?.[0]?.family ||
                resource.code?.text ||
                resource.display ||
                resource.id,
              disabled: resource.active === false,
            };
          });
        }

        return [];
      } catch (error) {
        console.error('FHIR data source error:', error);
        return [];
      }
    },
    [tenantId]
  );

  /**
   * Resolve API data source
   */
  const resolveAPI = useCallback(
    async (source: DataSource): Promise<OptionItem[]> => {
      if (!source.endpoint) {
        console.warn('API data source missing endpoint');
        return [];
      }

      try {
        // eslint-disable-next-line no-restricted-syntax -- API data sources are external endpoints
        const response = await fetch(source.endpoint, {
          method: source.method || 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-ID': tenantId || '',
            ...source.headers,
          },
          body: source.method === 'POST' && source.body ? JSON.stringify(source.body) : undefined,
        });

        if (!response.ok) {
          throw new Error(`API request failed: ${response.status}`);
        }

        const data = await response.json();

        // Apply transform if specified
        if (source.transform) {
          // Transform is a function name that should be globally available
          const transformFn = (window as unknown as Record<string, unknown>)[source.transform];
          if (typeof transformFn === 'function') {
            return transformFn(data);
          }
          console.warn(`Transform function not found: ${source.transform}`);
        }

        // Default transformation
        return transformGraphQLResponse(
          data,
          source.valuePath || 'id',
          source.labelPath || 'name'
        );
      } catch (error) {
        console.error('API data source error:', error);
        return [];
      }
    },
    [tenantId]
  );

  /**
   * Resolve computed data source
   */
  const resolveComputed = useCallback(
    async (source: DataSource, formValues?: Record<string, unknown>): Promise<OptionItem[]> => {
      if (!source.expression) {
        console.warn('Computed data source missing expression');
        return [];
      }

      try {
        // For now, use a simple eval-based approach
        // TODO: Implement proper CEL expression evaluation
        const expression = source.expression;

        // Create a context with form values
        const context = {
          values: formValues || {},
          tenantId,
        };

        // Try to evaluate as a function
        const fn = new Function('context', `return ${expression}`);
        const result = fn(context);

        if (Array.isArray(result)) {
          return result.map((item) => {
            if (typeof item === 'string') {
              return { value: item, label: item };
            }
            return {
              value: item.value ?? item.id ?? '',
              label: item.label ?? item.name ?? String(item.value),
            };
          });
        }

        return [];
      } catch (error) {
        console.error('Computed data source error:', error);
        return [];
      }
    },
    [tenantId]
  );

  /**
   * Main resolver function
   */
  const resolve = useCallback(
    async (source: DataSource): Promise<OptionItem[]> => {
      // Check cache first
      const cached = cacheRef.current.get(source);
      if (cached) {
        return cached;
      }

      let result: OptionItem[];

      switch (source.type) {
        case 'static':
          result = resolveStatic(source);
          break;
        case 'graphql':
          result = await resolveGraphQL(source);
          break;
        case 'fhir':
          result = await resolveFHIR(source);
          break;
        case 'api':
          result = await resolveAPI(source);
          break;
        case 'computed':
          result = await resolveComputed(source);
          break;
        default:
          console.warn(`Unknown data source type: ${source.type}`);
          result = [];
      }

      // Cache the result
      const ttl = source.cacheTTL || 60000; // Default 1 minute
      cacheRef.current.set(source, result, ttl);

      return result;
    },
    [resolveGraphQL, resolveFHIR, resolveAPI, resolveComputed]
  );

  return resolve;
}

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Hook for loading data source on mount
 */
export function useDataSource(source: DataSource | undefined): {
  data: OptionItem[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
} {
  const resolve = useDataSourceResolver();
  const [data, setData] = useState<OptionItem[]>([]);
  const [loading, setLoading] = useState(!!source);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    if (!source) return;

    setLoading(true);
    setError(null);

    try {
      // Invalidate cache
      globalCache.invalidate(source);
      const result = await resolve(source);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [source, resolve]);

  // Load on mount or when source changes
  useEffect(() => {
    if (source) {
      resolve(source)
        .then(setData)
        .catch((e: any) => setError(e instanceof Error ? e : new Error('Unknown error')))
        .finally(() => setLoading(false));
    }
  }, [source, resolve]);

  return { data, loading, error, refresh };
}

// ============================================================================
// EXPORTS
// ============================================================================

export { DataSourceCache, globalCache };
export type { DataSourceResolver };
